import { useState, useEffect, useCallback } from 'react';
import { useUI } from '../../context/AppContext.jsx';
import { usePlugins } from '../../hooks/usePlugins.js';
import { getAvailableRobots, getRobotParameters } from '../../kinematics/URRobotParameters.js';

export default function ValidationPanel() {
  const { showValidationPanel, setShowValidationPanel, currentRobotType } = useUI();
  const { validators, runAllValidatorsWithContext } = usePlugins();
  
  const [validationResults, setValidationResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedRobot, setSelectedRobot] = useState(currentRobotType);
  
  // Get available robots
  const availableRobots = getAvailableRobots();

  // Reset to current robot type when panel opens
  useEffect(() => {
    if (showValidationPanel) {
      setSelectedRobot(currentRobotType);
      setValidationResults(null); // Clear previous results
    }
  }, [showValidationPanel, currentRobotType]);

  // Helper function that takes robot type as parameter
  const runValidationForRobot = useCallback((robotType) => {
    setIsRunning(true);
    // Use setTimeout to allow UI to update before running validators
    setTimeout(() => {
      // Get robot parameters for the specified robot
      const robotKey = robotType.charAt(0).toUpperCase() + 
                       robotType.charAt(1).toUpperCase() + 
                       robotType.slice(2).toLowerCase();
      
      let robotParams;
      try {
        robotParams = getRobotParameters(robotKey);
      } catch (e) {
        // Fallback to UR10e if robot not found
        robotParams = getRobotParameters('UR10e');
      }
      
      const results = runAllValidatorsWithContext({
        robotType: robotType,
        robotReach: robotParams.reach,
        robotName: robotParams.name,
      });
      setValidationResults(results);
      setIsRunning(false);
    }, 0);
  }, [runAllValidatorsWithContext]);

  // Handler for re-run button uses selected robot
  const handleRunValidation = () => {
    runValidationForRobot(selectedRobot);
  };

  // Handle robot selection change - automatically rerun validation
  const handleRobotChange = (newRobot) => {
    setSelectedRobot(newRobot);
    runValidationForRobot(newRobot);
  };

  // Run validation when panel opens - use currentRobotType directly
  useEffect(() => {
    if (showValidationPanel && !validationResults && !isRunning) {
      runValidationForRobot(currentRobotType);
    }
  }, [showValidationPanel, currentRobotType, validationResults, isRunning, runValidationForRobot]);

  const handleClose = () => {
    setShowValidationPanel(false);
    setValidationResults(null);
  };

  if (!showValidationPanel) return null;

  const errorCount = validationResults?.results?.reduce(
    (count, r) => count + (r.valid ? 0 : r.messages.length),
    0
  ) || 0;

  const warningCount = validationResults?.results?.reduce(
    (count, r) => count + (r.severity === 'warning' ? r.messages.length : 0),
    0
  ) || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-bg-secondary rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Validation Results</h2>
          <button
            onClick={handleClose}
            className="text-text-muted hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        {isRunning ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3">Running validators...</span>
          </div>
        ) : validationResults ? (
          <div className="flex-1 overflow-auto">
            {/* Robot Selector */}
            <div className="mb-4 p-3 bg-bg-primary rounded border border-border-primary">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Validate for Robot:</label>
                <select
                  value={selectedRobot}
                  onChange={(e) => handleRobotChange(e.target.value)}
                  className="ml-3 px-3 py-1.5 bg-bg-secondary border border-border-primary rounded text-sm"
                >
                  {availableRobots.map((robot) => (
                    <option key={robot} value={robot.toLowerCase()}>
                      {robot} {robot.toLowerCase() === currentRobotType.toLowerCase() ? '(current)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              {selectedRobot.toLowerCase() !== currentRobotType.toLowerCase() && (
                <p className="text-xs text-yellow-400 mt-2">
                  ⚠️ Validating for {selectedRobot.toUpperCase()}, but {currentRobotType.toUpperCase()} is currently selected
                </p>
              )}
            </div>

            {/* Summary */}
            <div className={`p-4 rounded-lg mb-4 ${
              validationResults.valid 
                ? 'bg-green-900/30 border border-green-600' 
                : 'bg-red-900/30 border border-red-600'
            }`}>
              {validationResults.valid ? (
                <div className="flex items-center text-green-400">
                  <span className="text-2xl mr-2">✓</span>
                  <span className="font-semibold">All validations passed!</span>
                </div>
              ) : (
                <div className="flex items-center text-red-400">
                  <span className="text-2xl mr-2">✗</span>
                  <span className="font-semibold">
                    Found {errorCount} issue{errorCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>

            {/* Validator Results */}
            <div className="space-y-3">
              {validationResults.results.map((result, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded border ${
                    result.valid 
                      ? 'bg-bg-primary border-border-primary' 
                      : 'bg-red-900/20 border-red-600/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{result.name}</span>
                    <span className={`text-sm px-2 py-0.5 rounded ${
                      result.valid 
                        ? 'bg-green-600/30 text-green-400' 
                        : 'bg-red-600/30 text-red-400'
                    }`}>
                      {result.valid ? 'Passed' : 'Failed'}
                    </span>
                  </div>
                  
                  {result.messages.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {result.messages.map((message, msgIndex) => (
                        <li 
                          key={msgIndex}
                          className="text-sm text-text-muted flex items-start"
                        >
                          <span className="text-red-400 mr-2">•</span>
                          {message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-text-muted">
            Click "Run Validation" to check your waypoints
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-4 pt-4 border-t border-border-primary">
          <button
            onClick={handleRunValidation}
            disabled={isRunning}
            className="px-4 py-2 border border-border-primary rounded hover:bg-bg-primary disabled:opacity-50"
          >
            {isRunning ? 'Running...' : 'Re-run Validation'}
          </button>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-hover"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
